// 路由表在 ./route-table.ts（path → {feature, title, anyOf}）。
// 业务页加载机制：import.meta.glob 扫描 src/features/*\/index.tsx，
// 文件存在则 React.lazy 真实页面，缺失则渲染 PlaceholderPage——
// 后续页面智能体只需新建 src/features/<feature>/index.tsx（默认导出页面组件），路由零改动。

import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { ROUTE_TABLE } from "./route-table";
import { AppLayout } from "./features/shell/AppLayout";
import { RequireAuth, RequireCapability } from "./lib/auth";
import { Button, EmptyState, Spinner } from "@bf/ui";
import { PlaceholderPage } from "./features/_placeholder";
import { ForbiddenPage } from "./features/auth/ForbiddenPage";
import { ChangePasswordPage } from "./features/auth/ChangePasswordPage";
import { LoginCallbackPage } from "./features/auth/LoginCallbackPage";
import { LoginPage } from "./features/auth/LoginPage";

type PageModule = { default: ComponentType };

// vite 构建期静态分析：已存在的 features 页打独立 chunk，未存在的不出现在表里
const featurePages = import.meta.glob<PageModule>("./features/*/index.tsx");

function pageComponent(def: (typeof ROUTE_TABLE)[number]): LazyExoticComponent<ComponentType> | ComponentType {
  const loader = featurePages[`./features/${def.feature}/index.tsx`];
  if (loader) return lazy(loader);
  return () => <PlaceholderPage name={def.title} />;
}

function PageLoading() {
  return (
    <div className="flex items-center justify-center py-24">
      <Spinner />
    </div>
  );
}

// 404 页：与 403（ForbiddenPage）同一形态——整屏居中 + @bf/ui EmptyState + 主操作返回工作台。
function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] p-6">
      <EmptyState
        title="页面不存在"
        description="没有找到该路径对应的页面，链接可能已失效。"
        action={
          <Button variant="primary" onClick={() => navigate("/")}>返回工作台</Button>
        }
      />
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/callback" element={<LoginCallbackPage />} />
      <Route
        path="/change-password"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />
      <Route path="/403" element={<ForbiddenPage />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {ROUTE_TABLE.map((def) => {
          const Page = pageComponent(def);
          return (
            <Route
              key={def.path}
              path={def.path}
              element={
                <RequireCapability anyOf={def.anyOf}>
                  <Suspense fallback={<PageLoading />}>
                    <Page />
                  </Suspense>
                </RequireCapability>
              }
            />
          );
        })}
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
